import { useEffect, useState } from "react";
import { OAuthError, postData } from "../backend";
import { useModal } from "../contexts";
import useScript from "../hooks/useScript";
import { BLOCK_ICON_MAP, GOOGLE_MIMETYPE_MAP, GOOGLE_VIEW_ID_MAP } from "../utils";
import ErrorModal from "./ErrorModal";
import OAuthModal from "./OAuthModal";
import { UploadIcon } from "@heroicons/react/outline";
import FileUploadModal from "./FileUploadModal";
import { useTranslation } from "react-i18next";
import ESignatureLabel from "./ESginatureLabel";

interface Props {
  blockType: string;
  title: string;
  subtitle: string;
  openBlock: string | null;
  setOpenBlock: (s: string) => void;
  onSelection: (blockType: string, file?: any) => void;
  onSelectPdfFile: (blockType: string, file: File) => void;
  isSignable?: boolean;
  vertical?: boolean;
}

const scopes = ["https://www.googleapis.com/auth/drive"];

function WorkflowSelectPDFButton(props: Props) {
  const { openModal } = useModal();
  const { t } = useTranslation();

  const status = useScript('https://apis.google.com/js/api.js');
  const [pickerLoaded, setPickerLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (status !== "ready")
      return;

    // @ts-ignore
    const gapi = window.gapi;

    gapi.load('picker',
      () => setPickerLoaded(true)
    );
  }, [status]);

  async function handleOpenPicker() {

    function handlePickerSelection(pickerResult: any) {
      if (pickerResult.action === 'picked')
        props.onSelection(props.blockType, pickerResult.docs[0])
    }

    try {
      const accessToken = await postData("/auth/access-token/", { scopes });

      // @ts-ignore
      const google = window.google;

      const picker = new google.picker.PickerBuilder()
        .setAppId(process.env.REACT_APP_GOOGLE_APP_ID)
        .setDeveloperKey(process.env.REACT_APP_GOOGLE_API_KEY)
        .setOAuthToken(accessToken)
        .setSize(document.body.clientWidth, document.body.clientHeight)
        .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
        .setCallback(handlePickerSelection)

      const docsView = new google.picker.DocsView(google.picker.ViewId[GOOGLE_VIEW_ID_MAP["PDF"]])
      docsView.setMode(google.picker.DocsViewMode.LIST)
      picker.addView(docsView);

      const rootView = new google.picker.DocsView()
      rootView.setMode(google.picker.DocsViewMode.LIST)
      rootView.setMimeTypes(GOOGLE_MIMETYPE_MAP["PDF"])
      rootView.setIncludeFolders(true)
      rootView.setParent("root")
      picker.addView(rootView);

      const sharedView = new google.picker.DocsView()
      sharedView.setMode(google.picker.DocsViewMode.LIST)
      sharedView.setMimeTypes(GOOGLE_MIMETYPE_MAP["PDF"])
      sharedView.setIncludeFolders(true)
      sharedView.setEnableDrives(true)
      picker.addView(sharedView);

      picker.build()
        .setVisible(true);

    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={handleOpenPicker} />);
      else
        openModal(<ErrorModal details={error} />);
    }
  }

  if (props.openBlock === props.blockType)
    return (
      <div className={`flex flex-shrink-0 border-2 border-gray-200 rounded ${props.vertical ? "flex-col" : "flex-row"}`}>
        <button
          className={`group flex items-center w-full hover:bg-blue hover:bg-opacity-5 cursor-pointer p-4 ${props.vertical ? "border-b border-gray-200" : "border-r border-gray-200"}`}
          onClick={handleOpenPicker}
          disabled={!pickerLoaded}
        >
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center"><img className="max-w-full max-h-full" src={BLOCK_ICON_MAP["PDF"]} alt={"PDF"} /></div>
          <div className="text-sm group-disabled:text-gray-400 group-disabled:cursor-progress font-gilroy font-semibold text-gray-600 ml-4 text-left">
            {t("Select PDF from Google Drive")}
          </div>
        </button>
        <button
          className="flex items-center w-full hover:bg-blue hover:bg-opacity-5 cursor-pointer p-4"
          onClick={() => openModal(<FileUploadModal onUpload={file => props.onSelectPdfFile(props.blockType, file)} allowedFormats={['PDF']} />)}
        >
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center"><UploadIcon className="w-8 h-8 text-gray-600" /></div>
          <div className="text-sm font-gilroy font-semibold text-gray-600 ml-4 text-left">
            {t("Upload PDF file")}
          </div>
        </button>
      </div>
    );

  return (
    <button className="flex flex-shrink-0 items-center border-2 border-gray-200 hover:border-blue rounded cursor-pointer p-4" onClick={() => props.setOpenBlock(props.blockType)}>
      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center"><img className="max-w-full max-h-full" src={BLOCK_ICON_MAP["PDF"]} alt={"PDF"} /></div>
      <div className="flex flex-col items-start text-left ml-4">
        <div className="font-gilroy font-semibold text-base text-gray-600">
          {props.title}
        </div>
        <div className="font-gilroy font-semibold text-xs text-gray-400">
          {props.subtitle}
        </div>
      </div>
      {props.isSignable &&
        <ESignatureLabel />
      }
    </button>
  );
}

export default WorkflowSelectPDFButton;
